import type { ThemeColor } from '@vrplatform/theme';
import { day, hasValue } from '@vrplatform/utils';

type LockDetails = {
  booksClosed?: {
    date?: string | null;
  } | null;
  ownerStatement?: {
    ids?: readonly unknown[] | null;
  } | null;
  statementPeriod?: {
    hits?: readonly unknown[] | null;
  } | null;
};

type LockReasonSource = {
  isLocked: boolean;
  scopeBadge?: DrawerInfoCardLockBadge;
  lock?: LockDetails | null;
};

export type DrawerInfoCardLockBadge = {
  label: string;
  tooltip?: string;
  color?: ThemeColor;
};

const hasOwnerStatementReason = (lock?: LockDetails | null) =>
  !!lock?.ownerStatement?.ids?.length || !!lock?.statementPeriod?.hits?.length;

const getScopeBadge = (badge?: DrawerInfoCardLockBadge) =>
  badge
    ? {
        color: 'yellow' as const,
        ...badge,
      }
    : undefined;

const getBooksClosedBadge = (date?: string | null): DrawerInfoCardLockBadge => {
  if (!date || !day(date).isValid()) {
    return {
      label: 'Books closed',
      color: 'orange',
      tooltip: 'Locked because the books are closed.',
    };
  }

  return {
    label: 'Books closed',
    color: 'orange',
    tooltip: `Books are closed through ${day(date).subtract(1, 'day').format('LL')}.`,
  };
};

export const getDrawerInfoCardLockReasonBadges = ({
  sources,
  fallbackBadge = {
    label: 'Locked',
    color: 'portal.gray',
    tooltip: 'This item is locked.',
  },
}: {
  sources: LockReasonSource[];
  fallbackBadge?: DrawerInfoCardLockBadge;
}) => {
  const activeSources = sources.filter((source) => source.isLocked);

  if (!activeSources.length) return [];

  const booksClosedDate = activeSources.find(
    (source) => source.lock?.booksClosed?.date
  )?.lock?.booksClosed?.date;

  const badges = [
    ...activeSources.map((source) => getScopeBadge(source.scopeBadge)),
    activeSources.some((source) => hasOwnerStatementReason(source.lock))
      ? {
          label: 'Owner Statement',
          color: 'blue',
          tooltip: 'Locked due to a related published owner statement.',
        }
      : undefined,
    booksClosedDate ? getBooksClosedBadge(booksClosedDate) : undefined,
  ].filter(hasValue);

  return badges.length
    ? Array.from(new Map(badges.map((badge) => [badge.label, badge])).values())
    : [fallbackBadge];
};
